import criticalIcon from '@assets/svg/New_Severity_Icons/Critical.svg';
import highIcon from '@assets/svg/New_Severity_Icons/High.svg';
import lowIcon from '@assets/svg/New_Severity_Icons/Low.svg';
import mediumIcon from '@assets/svg/New_Severity_Icons/Medium.svg';
import ICONS from '@pages/visualization/ForceGraph/icon-mapping';
import { CATEGORICAL_COLORS } from '@root/utilities/pastelColors';
import { RISK_SIGNALS, severityColor } from './findingConfig';

// Lays the finding's path (workload → image → package → CVE) out for FinOpsVisualizerGraph, in the
// { nodes, links, badges } shape its own mapper produces. Same spacing as the FinOps chain.

const STEP_X = 195;
const MAX_LABEL_LENGTH = 24;

const truncate = text => (text.length > MAX_LABEL_LENGTH ? `${text.slice(0, MAX_LABEL_LENGTH - 1)}…` : text);

// The workload uses the shared resource icon for its kind; the other hops are lucide glyphs on a pastel disc,
// like the FinOps branch nodes.
const workloadResourceType = kind => (ICONS[`AWS.EKS.${kind}`] ? `AWS.EKS.${kind}` : 'AWS.EKS.Pod');

const SEVERITY_ICONS = { critical: criticalIcon, high: highIcon, medium: mediumIcon, low: lowIcon };

// The small icons under a node: the shared severity icon, or the risk's lucide glyph in its colour.
const toBadges = (keys = [], severity) =>
  keys
    .map(key => {
      if (key === 'severity') {
        const href = SEVERITY_ICONS[severity];
        return href && { href, label: `${severity.charAt(0).toUpperCase()}${severity.slice(1)} severity` };
      }
      const signal = RISK_SIGNALS[key];
      return signal && { icon: signal.glyph, color: signal.color, label: signal.label };
    })
    .filter(Boolean);

const GLYPHS = {
  image: { icon: 'lucide-layers', fill: CATEGORICAL_COLORS.purple },
  package: { icon: 'lucide-package', fill: CATEGORICAL_COLORS.teal },
  cve: { icon: 'lucide-bug' },
  // A node type the API adds later still draws, on a neutral disc.
  fallback: { icon: 'lucide-circle-alert', fill: CATEGORICAL_COLORS.mutedBlue },
};

const capitalize = text => (text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : '');

// Each API node type as a path step: its label, sublabel and the risk badges its data supports.
const STEP_BUILDERS = {
  workload: ({ name, data }) => ({
    name: data.workload_name ?? name,
    sublabel: `Kubernetes ${data.workload_kind ?? 'Workload'}`,
    workloadKind: data.workload_kind,
    badges: [data.is_public_exposed && 'internet_ingress', data.total_vulnerabilities > 1 && 'vulnerabilities'],
  }),
  image: ({ name }) => ({ name, sublabel: 'Image', badges: [] }),
  package: ({ name, data }) => ({
    name: data.name ?? name,
    sublabel: data.version ? `Package ${data.version}` : 'Package',
    badges: [data.in_use && 'in_use'],
  }),
  cve: ({ name, data }) => ({
    name: data.vulnerability_id ?? name,
    sublabel: `${capitalize(data.severity?.toLowerCase())} CVE`.trim(),
    badges: ['severity', data.exploitable && 'known_exploit', data.fixable && 'fixable'],
  }),
};

// Follows `relation` from the node nothing points to, so the steps read workload → … → CVE whatever order
// the API lists the nodes in. Nodes the walk never reaches are kept, in API order, after it.
const orderNodes = (nodes, relation) => {
  const byId = new Map(nodes.map(node => [node.id, node]));
  const next = new Map(relation.map(({ source, target }) => [source, target]));
  const targets = new Set(relation.map(({ target }) => target));
  const ordered = [];
  const visited = new Set();

  for (let id = nodes.find(node => !targets.has(node.id))?.id; byId.has(id) && !visited.has(id); id = next.get(id)) {
    visited.add(id);
    ordered.push(byId.get(id));
  }
  return [...ordered, ...nodes.filter(node => !visited.has(node.id))];
};

// The findings graph API ({ nodes, relation }) as the `{ path, severity }` drawn below. A package hop is
// marked fixable when the CVE has a fix, as that is the hop the fix upgrades.
export const toFindingPath = ({ nodes = [], relation = [] } = {}) => {
  const cve = nodes.find(node => node.type === 'cve')?.data;
  const severity = cve?.severity?.toLowerCase();

  const path = orderNodes(nodes, relation).map(node => {
    const build = STEP_BUILDERS[node.type] ?? (({ name }) => ({ name, sublabel: capitalize(node.type), badges: [] }));
    const step = build({ name: node.name ?? node.id, data: node.data ?? {} });
    if (node.type === 'package' && cve?.fixable) step.badges.push('fixable');
    return { ...step, id: node.id, kind: node.type, badges: step.badges.filter(Boolean) };
  });

  return { path, severity };
};

export const mapFindingPathGraph = ({ path = [], severity } = {}) => {
  if (!path.length) return null;

  const nodes = path.map((step, index) => {
    const base = {
      id: step.id,
      x: index * STEP_X,
      y: 0,
      label: truncate(step.name),
      sublabel: step.sublabel,
      badges: toBadges(step.badges, severity),
    };
    if (step.kind === 'workload') return { ...base, resourceType: workloadResourceType(step.workloadKind) };

    const glyph = GLYPHS[step.kind] ?? GLYPHS.fallback;
    // White glyph on the disc, as on the FinOps branch nodes; the CVE disc takes its severity's colour.
    return { ...base, icon: glyph.icon, color: '#FFFFFF', fill: glyph.fill ?? severityColor(severity) };
  });

  const links = nodes.slice(1).map((node, index) => ({ source: nodes[index].id, target: node.id, arrow: 'end' }));

  return { nodes, links, badges: [] };
};
